// src/components/hospitalizacion/EvolucionCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Stack
} from '@mui/material';
import { ShowChart, MedicalServices, Visibility, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import api from '../../api/api';
import dayjs from 'dayjs';

const ITEMS_PER_PAGE = 5;

const EvolucionCard = ({ hospitalizacionId }) => {
  const [evoluciones, setEvoluciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1); // 1-based
  const [selectedEvolucion, setSelectedEvolucion] = useState(null);

  useEffect(() => {
    if (hospitalizacionId) {
      fetchEvoluciones();
    }
  }, [hospitalizacionId]);

  const fetchEvoluciones = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/hospitalizacion-evolucion/${hospitalizacionId}?limit=50`);
      // Orden descendente (más reciente primero)
      const sorted = (res.data.items || res.data).sort((a, b) =>
        dayjs(b.fecha_hora).diff(dayjs(a.fecha_hora))
      );
      setEvoluciones(sorted);
      setPage(1); // Resetear a página 1 al recargar
    } catch (err) {
      console.error('Error cargando evoluciones:', err);
      setEvoluciones([]);
    } finally {
      setLoading(false);
    }
  };

  // Paginación
  const totalPages = Math.ceil(evoluciones.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const paginatedEvoluciones = evoluciones.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (direction) => {
    if (direction === 'prev' && page > 1) setPage(page - 1);
    if (direction === 'next' && page < totalPages) setPage(page + 1);
  };

  const handleVerDetalles = (evolucion) => {
    setSelectedEvolucion(evolucion);
  };

  const handleCloseDetalles = () => {
    setSelectedEvolucion(null);
  };

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={30} />
        </Box>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <Box sx={{ p: 2, bgcolor: '#475569', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ShowChart sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              ÚLTIMAS EVOLUCIONES ({evoluciones.length})
            </Typography>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell>FECHA Y HORA</TableCell>
                <TableCell align="center">VITALES</TableCell>
                <TableCell align="center">DOLOR</TableCell>
                <TableCell>OBSERVACIONES</TableCell>
                <TableCell align="right">Detalles</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEvoluciones.map((e) => (
                <TableRow key={e.id_evolucion} hover>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {dayjs(e.fecha_hora).format('DD/MM/YYYY hh:mm A')} {/* 12h con AM/PM */}
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color={e.temperatura > 39.1 ? 'error' : 'primary'}>
                      {e.temperatura ? `${e.temperatura}°C` : '—'}
                    </Typography>
                    <Typography variant="caption">
                      FC: {e.fc || '—'} | FR: {e.fr || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Badge badgeContent={e.score_dolor ?? '—'} color={e.score_dolor >= 5 ? 'error' : 'success'}>
                      <MedicalServices sx={{ fontSize: 22, color: '#94a3b8' }} />
                    </Badge>
                  </TableCell>

                  <TableCell sx={{ fontSize: '0.78rem', maxWidth: 280 }}>
                    {e.observaciones?.substring(0, 60) || 'Sin observaciones'}
                    {e.observaciones?.length > 60 && '...'}
                  </TableCell>

                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleVerDetalles(e)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {evoluciones.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Aún no hay evoluciones registradas
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                disabled={page === 1}
                onClick={() => handlePageChange('prev')}
              >
                <KeyboardArrowLeft />
              </IconButton>

              <Typography variant="body2">
                Página {page} de {totalPages}
              </Typography>

              <IconButton
                disabled={page === totalPages}
                onClick={() => handlePageChange('next')}
              >
                <KeyboardArrowRight />
              </IconButton>
            </Stack>
          </Box>
        )}
      </Card>

      {/* Modal de detalles completos */}
      <Dialog
        open={!!selectedEvolucion}
        onClose={handleCloseDetalles}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1e293b', color: 'white' }}>
          Detalles de Evolución - {selectedEvolucion && dayjs(selectedEvolucion.fecha_hora).format('DD/MM/YYYY hh:mm A')}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">Temperatura</Typography>
              <Typography>{selectedEvolucion?.temperatura ? `${selectedEvolucion.temperatura}°C` : '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">FC / FR</Typography>
              <Typography>FC: {selectedEvolucion?.fc || '—'} | FR: {selectedEvolucion?.fr || '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">SpO₂</Typography>
              <Typography>{selectedEvolucion?.spo2 ? `${selectedEvolucion.spo2}%` : '—'}</Typography>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">PA Sistólica / Diastólica</Typography>
              <Typography>{selectedEvolucion?.pa_sistolica || '—'} / {selectedEvolucion?.pa_diastolica || '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">PAM</Typography>
              <Typography>{selectedEvolucion?.pam || '—'} mmHg</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">CRT</Typography>
              <Typography>{selectedEvolucion?.crt ? `${selectedEvolucion.crt} seg` : '—'}</Typography>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">Mucosas</Typography>
              <Typography>{selectedEvolucion?.mucosas || '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">Score Dolor</Typography>
              <Typography>{selectedEvolucion?.score_dolor ?? '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">Glucosa</Typography>
              <Typography>{selectedEvolucion?.glucosa ? `${selectedEvolucion.glucosa} mg/dL` : '—'}</Typography>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">Lactato</Typography>
              <Typography>{selectedEvolucion?.lactato ? `${selectedEvolucion.lactato} mmol/L` : '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">Peso Actual</Typography>
              <Typography>{selectedEvolucion?.peso_actual ? `${selectedEvolucion.peso_actual} kg` : '—'}</Typography>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Typography variant="subtitle2">Diuresis</Typography>
              <Typography>{selectedEvolucion?.diuresis_ml_kg_h ? `${selectedEvolucion.diuresis_ml_kg_h} ml/kg/h` : '—'}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Actitud</Typography>
              <Typography>{selectedEvolucion?.actitud || '—'}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Emesis (número)</Typography>
              <Typography>{selectedEvolucion?.emesis_count ?? '—'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2">Deposiciones</Typography>
              <Typography>{selectedEvolucion?.deposiciones || '—'}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle2">Apetito</Typography>
              <Typography>{selectedEvolucion?.apetito || '—'}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Observaciones</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedEvolucion?.observaciones || 'Sin observaciones'}
              </Typography>
            </Grid>

            {selectedEvolucion?.metadatos && Object.keys(selectedEvolucion.metadatos).length > 0 && (
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Metadatos adicionales</Typography>
                <pre style={{ background: '#f1f5f9', padding: 12, borderRadius: 6, fontSize: '0.85rem' }}>
                  {JSON.stringify(selectedEvolucion.metadatos, null, 2)}
                </pre>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetalles} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EvolucionCard;